"use client";

import React, { useEffect, useState } from "react";
import { Autocomplete, TextField, Typography } from "@mui/material";
import { useTranslations } from "next-intl";
import { ControllerFieldState, ControllerRenderProps } from "react-hook-form";
import { ExperianResult } from "@/types/experian";
import { RABFormData } from "@/types/leads/lead-forms";

interface AddressFieldProps {
  handleChange: (value: string) => Promise<void>;
  addressOptions: ExperianResult[];
  testId: string;
  field?: ControllerRenderProps<RABFormData, "experianAddress">;
  fieldState?: ControllerFieldState;
  required?: boolean;
  borderRadius?: string;
}

const AutocompleteAddressField = ({
  handleChange,
  addressOptions,
  testId,
  field,
  fieldState,
  borderRadius,
  required = true
}: AddressFieldProps) => {
  const t = useTranslations("FORMS");
  const [inputValue, setInputValue] = useState(field?.value?.suggestion ?? "");

  // Sync inputValue with form value (for autofill)
  useEffect(() => {
    setInputValue(field?.value?.suggestion ?? "");
  }, [field?.value?.suggestion]);

  return (
    <Autocomplete
      {...field}
      data-testid={`${testId}_autocomplete`}
      value={field?.value?.suggestion}
      inputValue={inputValue}
      options={addressOptions.map((o) => o.suggestion)}
      noOptionsText={<Typography color="text.primary">{t("Enter more address info")}</Typography>}
      defaultValue=""
      onInputChange={async (_, value) => {
        setInputValue(value);
        if (!value) {
          field?.onChange(undefined);
          return;
        }
        const newValue = addressOptions.find((v) => v.suggestion === value);
        field?.onChange(newValue);
        await handleChange(newValue?.suggestion ?? value);
      }}
      onChange={(_, value) => {
        if (!value) return;
        const newValue = addressOptions.find((v) => v.suggestion === value);
        // When user selects an option
        field?.onChange(newValue);
      }}
      renderInput={(params) => (
        <TextField
          data-testid={`${testId}_text-field`}
          {...params}
          required={required}
          label={t("Address")}
          error={!!fieldState?.error}
          helperText={fieldState?.error?.message}
          aria-label={t("Address")}
          placeholder={t("Address")}
          multiline
          sx={{
            "& .MuiOutlinedInput-root": { borderRadius }
          }}
        />
      )}
    />
  );
};

export default AutocompleteAddressField;
